import { Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { IoClose } from 'react-icons/io5';
import { TrialProgramImage } from '@ui';
import {
  TrialAnsweredQuestionModel,
  TrialQuestion,
  OrganType,
  CaseStatus,
  ReallocationData,
  OutcomeType,
} from '@/api/apis/case';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { TrialQASection } from './TrialQASection';
import { EditSCSMethod } from './EditSCSMethod';
import { Uuid } from '@/api/types';
import { isAllowedStatus, isHeartTypeA } from '@/utils/helpers';

type TrialQuestionnaireResultsModalProps = {
  caseId: Uuid;
  caseStatus: CaseStatus;
  onClose: () => void;
  trialProgram: TrialProgram;
  questionnaire: TrialAnsweredQuestionModel[];
  reallocationData: ReallocationData | null | undefined;
  hasEditPermission: boolean;
};

export const TrialQuestionnaireResultsModal = ({
  caseId,
  caseStatus,
  reallocationData,
  onClose,
  trialProgram,
  questionnaire,
  hasEditPermission,
}: TrialQuestionnaireResultsModalProps) => {
  const isDryRunReallocation = reallocationData?.outcomeData?.outcomeType === OutcomeType.DryRun;
  const heartPartA = isHeartTypeA(questionnaire, trialProgram.organ);
  const allowedByStatusRegular = isAllowedStatus({
    statusFrom: CaseStatus.ClinicalScreening,
    statusTo: CaseStatus.OrganOnOCS,
    currentStatus: caseStatus,
  });
  const allowedByStatusReallocation = isAllowedStatus({
    statusFrom: CaseStatus.ClinicalScreening,
    statusTo: CaseStatus.Accepted,
    currentStatus: caseStatus,
  });
  const allowEditingByStatus = reallocationData
    ? isDryRunReallocation && allowedByStatusReallocation
    : allowedByStatusRegular;
  const scsMethodEditingDisabled = !hasEditPermission || !allowEditingByStatus;

  return (
    <Modal
      className="scroll"
      isOpen
      onClose={onClose}
      size="xl"
      closeButton={<IoClose size={42} className="z-1000" />}
      classNames={{
        closeButton: 'text-base-10 mt-6 mr-4',
        header: 'text-hl! flex flex-col gap-4 text-base-20 pt-6 pb-4',
        body: 'flex flex-col mx-6 p-0 gap-6',
      }}
    >
      <ModalContent>
        <ModalHeader>
          <div className="flex gap-2 items-center">
            <div className="font-secondary">{trialProgram.name} Program</div>
            <TrialProgramImage organ={trialProgram.organ} />
          </div>
          <Divider />
        </ModalHeader>
        <ModalBody>
          {trialProgram.organ === OrganType.Lungs && (
            <>
              <TrialQASection
                label="DENOVO Trial Program"
                question={TrialQuestion.RequestedRecoveryForDenovoTrial}
                questionnaire={questionnaire}
              />
              <EditSCSMethod
                caseId={caseId}
                storageQuestion={TrialQuestion.DenovoColdStaticPreservationConfirmation}
                questionnaire={questionnaire}
                disabled={scsMethodEditingDisabled}
                onClose={onClose}
              />
            </>
          )}
          {trialProgram.organ === OrganType.Heart && (
            <>
              <TrialQASection
                label="ENHANCE Trial Program"
                question={TrialQuestion.RequestedRecoveryForEnhanceTrial}
                questionnaire={questionnaire}
              />
              <TrialQASection
                label="Trial Scenario"
                question={TrialQuestion.TypeOfEnhanceTrial}
                questionnaire={questionnaire}
              />
              {!heartPartA && (
                <EditSCSMethod
                  caseId={caseId}
                  storageQuestion={TrialQuestion.EnhanceColdStaticPreservationConfirmation}
                  questionnaire={questionnaire}
                  disabled={scsMethodEditingDisabled}
                  onClose={onClose}
                />
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};
